import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import {
  ContextBasedGeneratorJob,
  QuestionGeneratorJob,
  TopicBasedGeneratorJob,
} from '../../../../../../utils/types/content_manager_types';
import { QuestionsList } from '../../../../upload_file/question_list';

export interface DocumentDetailsProps {
  job: TopicBasedGeneratorJob | ContextBasedGeneratorJob;
  updateJob: (newJobData: QuestionGeneratorJob) => void;
}

export const DocumentDetails = ({ job, updateJob }: DocumentDetailsProps) => {
  const sourceText =
    'transcript' in job && job.transcript
      ? job.transcript
      : job.topic || '';
  const transcriptPreview =
    sourceText.length > 900
      ? `${sourceText.slice(0, 900).trim()}...`
      : sourceText;
  const wordCount = sourceText.trim()
    ? sourceText.trim().split(/\s+/).length
    : 0;
  const jobTitle =
    typeof job.settings?.jobTitle === 'string'
      ? job.settings.jobTitle
      : job.settings?.jobTitle?.jobTitle;

  const summaryCards = [
    {
      label: 'Source',
      value:
        'filename' in job && job.filename
          ? job.filename
          : job.topic || 'Topic based generation',
    },
    {
      label: 'Generated Questions',
      value: String(job.questions?.length || 0),
    },
    {
      label: 'Question Type',
      value: job.settings?.questionType || 'Not set',
    },
    {
      label: 'Job Title',
      value: jobTitle || 'Not selected',
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 1.5,
          mb: 2,
        }}
      >
        {summaryCards.map((card, idx) => (
          <Paper
            key={idx}
            variant='outlined'
            sx={{
              p: 1.25,
              borderRadius: 2,
              bgcolor: '#ffffff',
              borderColor: 'divider',
              minWidth: 0,
            }}
          >
            <Typography
              variant='caption'
              fontWeight={800}
              letterSpacing='0.04em'
              textTransform='uppercase'
              color='text.secondary'
              display='block'
            >
              {card.label}
            </Typography>
            <Typography
              variant='body2'
              fontWeight={700}
              color='text.primary'
              noWrap
              display='block'
              sx={{ mt: 0.25 }}
            >
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {sourceText && (
        <Paper
          variant='outlined'
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: '#ffffff',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1.25,
            }}
          >
            <Box>
              <Typography variant='subtitle2' fontWeight={800} color='text.primary'>
                Transcript Preview
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {wordCount} words extracted from this content.
              </Typography>
            </Box>
            <Chip
              label={job.generationType || 'context'}
              size='small'
              sx={{
                bgcolor: '#ecfdf5',
                color: '#047857',
                fontWeight: 700,
                textTransform: 'capitalize',
              }}
            />
          </Box>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 6,
              overflow: 'hidden',
              px: 2,
              py: 1.5,
              lineHeight: 1.6,
              fontSize: '0.75rem',
            }}
          >
            {transcriptPreview}
          </Typography>
        </Paper>
      )}

      <QuestionsList
        job={job}
        setJob={updateJob}
        errorJob={false}
        questionsLoading={false}
      />
    </Box>
  );
};
